import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, PencilLine, Loader2 } from 'lucide-react';
import { sectionMeta } from '../lib/section-meta';
import { springs } from '../lib/utils-app';
import { bookmarkStore, attemptStore, recentStore } from '../lib/storage';
import { Button } from '../components/ui/button';
import QuestionCard from '../components/QuestionCard';
import { RevealFlow } from '../components/RevealFlow';
import FilterBar from '../components/FilterBar';
import Scratchpad from '../components/Scratchpad';
import api from '../lib/api';
import SolveItLink from '../components/SolveItLink';
import { solveitForTopic } from '../lib/solveit';

function useQuestionState(qId) {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const reset = useCallback(() => { setSelected(null); setRevealed(false); setShowSolution(false); }, []);
  useEffect(() => { reset(); }, [qId, reset]);
  return { selected, setSelected, revealed, setRevealed, showSolution, setShowSolution, reset };
}

export default function TopicPage() {
  const { subjectId, sectionId, topicId } = useParams();
  const navigate = useNavigate();
  const meta = sectionMeta(sectionId, subjectId);

  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(null);
  const [topicName, setTopicName] = useState('');
  const [sectionName, setSectionName] = useState('');

  const [idx, setIdx] = useState(0);
  const [filterDiff, setFilterDiff] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [scratchOpen, setScratchOpen] = useState(false);
  const [bookTick, setBookTick] = useState(0);

  // Load questions (large page so all topic questions are fetched at once — topics tend to be <80 questions)
  useEffect(() => {
    let alive = true;
    setQuestions(null);
    setError(null);
    api.listQuestions({ subject: subjectId, section: sectionId, topic: topicId, size: 100 })
      .then((data) => {
        if (!alive) return;
        setQuestions(data.items);
        if (data.items.length > 0) {
          setTopicName(data.items[0].topic);
          setSectionName(data.items[0].section);
        }
      })
      .catch((err) => alive && setError(err.message || 'Failed to load'));
    return () => { alive = false; };
  }, [subjectId, sectionId, topicId]);

  // Apply client-side filters
  const filtered = useMemo(() => {
    if (!questions) return [];
    return questions.filter((q) =>
      (filterDiff === 'all' || q.difficulty === filterDiff) &&
      (filterYear === 'all' || String(q.year) === filterYear)
    );
  }, [questions, filterDiff, filterYear]);

  const years = useMemo(() => {
    if (!questions) return [];
    return Array.from(new Set(questions.map((q) => String(q.year || '')).filter(Boolean)))
      .sort((x, y) => Number(y) - Number(x));
  }, [questions]);

  // Reset to first question when filters change
  useEffect(() => { setIdx(0); }, [subjectId, sectionId, topicId, filterDiff, filterYear]);

  const q = filtered[idx];
  const { selected, setSelected, revealed, setRevealed, showSolution, setShowSolution } = useQuestionState(q ? q.id : 'none');

  // Track recent topic
  useEffect(() => {
    if (sectionName && topicName) {
      recentStore.push({ subjectId, sectionId, topicId, sectionName, topicName });
    }
  }, [subjectId, sectionId, topicId, sectionName, topicName]);

  const onPick = useCallback((letter) => {
    if (!q || revealed) return;
    setSelected(letter);
    attemptStore.set(q.id, letter);
  }, [q, revealed, setSelected]);

  const reveal = useCallback(() => setRevealed(true), [setRevealed]);
  const showSol = useCallback(() => setShowSolution(true), [setShowSolution]);
  const hideSol = useCallback(() => setShowSolution(false), [setShowSolution]);
  const goNext = useCallback(() => setIdx((i) => Math.min(filtered.length - 1, i + 1)), [filtered.length]);
  const goPrev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const goTo = useCallback((i) => setIdx(i), []);

  const isBookmarked = useMemo(() => (q ? bookmarkStore.has(q.id) : false), [q, bookTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleBookmark = useCallback(() => { if (q) { bookmarkStore.toggle(q.id); setBookTick((n) => n + 1); } }, [q]);

  if (error) {
    return (
      <div className="text-center py-24 text-rose-300">
        Failed to load: {error}
        <div className="mt-3"><Link to="/" className="text-indigo-300 underline">Return to library</Link></div>
      </div>
    );
  }
  if (!questions) {
    return (
      <div className="rounded-2xl glass hairline p-16 flex items-center justify-center text-slate-400 gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
      </div>
    );
  }
  if (questions.length === 0) {
    return <div className="text-center py-24 text-slate-500">No questions found. <Link to="/" className="text-indigo-300">Library</Link></div>;
  }

  let flowState = 'pre';
  if (revealed) flowState = showSolution ? 'solution' : 'revealed';
  const correctLetter = q?.correctAnswer ? String(q.correctAnswer).toUpperCase().trim() : null;
  const isCorrect = Boolean(q && correctLetter && selected === correctLetter);

  return (
    <div>
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-[12.5px] text-slate-500 hover:text-slate-200 transition mb-5">
        <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{sectionName} · Chapter</div>
          <h1 className="font-serif-q text-[30px] md:text-[34px] tracking-tight text-slate-50 leading-tight">{topicName}</h1>
          {subjectId === 'math' && topicName && (
            <SolveItLink lead="Also preparing for JEE Main?" href={solveitForTopic(topicId).href}>
              {solveitForTopic(topicId).name} PYQs on SolveIT
            </SolveItLink>
          )}
        </div>
        <FilterBar filterDiff={filterDiff} setFilterDiff={setFilterDiff} filterYear={filterYear} setFilterYear={setFilterYear} years={years} />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 rounded-2xl glass hairline">No questions match the current filters.</div>
      ) : (
        <>
          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 18, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.985 }}
              transition={{ ...springs.soft, opacity: { duration: 0.25 } }}
            >
              <QuestionCard
                question={q}
                sectionName={sectionName}
                sectionAccent={meta.accent}
                selected={selected}
                revealed={revealed}
                isBookmarked={isBookmarked}
                onPick={onPick}
                onToggleBookmark={toggleBookmark}
                index={idx}
                total={filtered.length}
              />
              <RevealFlow
                state={flowState}
                isCorrect={isCorrect}
                correctLetter={correctLetter}
                selected={selected}
                solution={q.solution}
                hasOptions={!!q.hasOptions}
                hasCorrectAnswer={!!correctLetter}
                hasSolution={!!q.solution}
                onReveal={reveal}
                onShowSolution={showSol}
                onHideSolution={hideSol}
              />
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-7">
            <Button variant="outline" onClick={goPrev} disabled={idx === 0}
              className="border-white/8 bg-white/[0.03] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <PageDots total={filtered.length} idx={idx} goTo={goTo} />
            <Button onClick={goNext} disabled={idx === filtered.length - 1}
              className="bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-medium disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}

      <button
        onClick={() => setScratchOpen(true)}
        className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 h-11 rounded-full bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-md border border-white/10 text-slate-200 text-[12.5px] font-medium tracking-wide shadow-2xl"
      >
        <PencilLine className="w-4 h-4 text-indigo-300" />
        Scratchpad
      </button>
      <Scratchpad open={scratchOpen} onClose={() => setScratchOpen(false)} />
    </div>
  );
}

function PageDots({ total, idx, goTo }) {
  // For long topic lists, cap to 24 dots for sanity
  if (total > 24) {
    return (
      <div className="text-[11.5px] font-mono text-slate-400 tabular-nums">
        {String(idx + 1).padStart(2, '0')} <span className="text-slate-600">/</span> {String(total).padStart(2, '0')}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={`dot-${i}`}
          onClick={() => goTo(i)}
          aria-label={`Go to question ${i + 1}`}
          className={`h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-indigo-400' : 'w-2 bg-white/15 hover:bg-white/30'}`}
        />
      ))}
    </div>
  );
}
